import { useState } from "react";
import { Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

// @ts-ignore
import * as Sentry from "@sentry/react";

import UserAccountRoute from "components/auth/UserAccountRoute";
import LoaderProvider from "components/context/LoaderContext";
import SidebarLayout from "components/molecules/sidebar/SidebarLayout";
import GlobalMementoModalProvider from "components/GlobalMementoModalProvider";
import UserProvider from "components/context/UserContext";

import SignIn from "pages/signin";
import CreateAccountPage from "pages/CreateUserAccount";
// import MerchDropPage from "pages/MerchDrop";
import BrandPublicProfile from "pages/BrandPublicProfile";
import BrandSettings from "pages/BrandSettings";
import BrandOnBoardingPage from "pages/BrandOnboarding";
import CreateMagnetPage from "pages/CreateMagnet";
import MagnetPage from "pages/MagnetPage";
import Navbar from "components/pageComponents/Navbar/Navbar";
import BrandProvider from "components/context/BrandContext";
import BrandSubmissionSuccessForm from "components/organisms/BrandSubmissionSuccessForm";
import BrandDashboard from "pages/BrandDashboard";
import BrandRoute from "components/auth/BrandRoute";
import EditMagnetPage from "pages/BrandSettings/EditMagnet";
import ComingSoon from "pages/ComingSoon";
import PrivacyPolicy from "pages/PrivacyPolicy";
import TermsConditions from "pages/TermsConditions";
import { BrandDiscover } from "pages/BrandDiscover";
import NotFound from "pages/NotFound";
import UserProfilePage from "pages/UserProfile";
import AuthenticatedRoute from "components/auth/AuthenticatedRoute";
import ProfileSettings from "pages/UserSettings";
import UserPublicProfilePage from "pages/UserPublicProfile";
// import ClaimCode from "pages/ClaimCode";
import ShareRequest from "pages/ShareRequest";

import NotificationPage from "pages/Notification";
import PollPage from "pages/Poll";
import { WalkthroughProvider } from "pages/UserProfile/walkthrough/WalkthroughContext";
import Walkthrough from "pages/UserProfile/walkthrough/Walkthrough";

import Analytics from "pages/Analytics";
import AirdropMemento from "pages/AirdropMemento";
import ClaimMagnet from "pages/MagnetPage/ClaimMagnet";
import TwitterAuthorizationStatus from "pages/TwitterAuthorizationStatus";
import GoogleSigin from "pages/GoogleSignin";
import DataProvider from "pages/MagnetDetails/DataProvider";
import StripeContext from "components/context/StripeContext";
import { ROUTES } from "./constants";

// Docs https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const queryClient = new QueryClient();

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={
        <p className="grid h-[100vh] w-[100vw] place-content-center bg-black text-2xl text-white">
          Something Went Wrong!
        </p>
      }
      showDialog
    >
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen />
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          toastStyle={{ backgroundColor: "#060F1B" }}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />
        <LoaderProvider>
          <StripeContext>
            <SentryRoutes>
              <Route path={ROUTES.TWITTER_AUTHORIZATION} element={<TwitterAuthorizationStatus />} />
              <Route
                path="*"
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                element={<AppWithLayout />}
              />
            </SentryRoutes>
          </StripeContext>
        </LoaderProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

function AppWithLayout() {
  const [isSidebarCustomized, setIsSidebarCustomized] = useState(false);

  return (
    <UserProvider>
      <BrandProvider>
        <main className="">
          <Navbar isSidebarCustomized={isSidebarCustomized} />
          <WalkthroughProvider>
            <GlobalMementoModalProvider>
              <Walkthrough />
              <SidebarLayout isSidebarCustomized={isSidebarCustomized}>
                <SentryRoutes>
                  {/* Public Routes */}
                  <Route path={ROUTES.CLAIM_MAGNET} element={<ClaimMagnet />} />

                  <Route path={ROUTES.GOOGLE_SIGNIN} element={<GoogleSigin />} />
                  <Route path={ROUTES.SIGNIN} element={<SignIn />} />
                  <Route path={ROUTES.CREATE_USER_ACCOUNT} element={<CreateAccountPage />} />
                  <Route path={ROUTES.NOT_FOUND} element={<NotFound />} />
                  <Route path={`${ROUTES.COMING_SOON}`} element={<ComingSoon />} />
                  <Route path={`${ROUTES.PRIVACY_POLICY}`} element={<PrivacyPolicy />} />
                  <Route path={`${ROUTES.USER_PUBLIC_PROFILE}/:userId`} element={<UserPublicProfilePage />} />
                  <Route path={`${ROUTES.BRAND_PUBLIC_PROFILE}/:brandId`} element={<BrandPublicProfile />} />
                  <Route path={ROUTES.BRANDS_DISCOVER} element={<BrandDiscover />} />
                  <Route
                    path={ROUTES.MAGNET_PAGE}
                    element={<MagnetPage setIsSidebarCustomized={setIsSidebarCustomized} />}
                  />
                  <Route path={`${ROUTES.TERMS_AND_CONDITIONS}`} element={<TermsConditions />} />
                  <Route path={ROUTES.BRANDS_SIGNUP_SUCCESS} element={<BrandSubmissionSuccessForm />} />

                  <Route element={<UserAccountRoute />}>
                    {/* User Specific Routes. */}
                    <Route element={<AuthenticatedRoute />}>
                      {/* This route corresponds to base path '/' */}
                      <Route path={`${ROUTES.PROFILE_SETTING}`} element={<ProfileSettings />} />
                      <Route path={`${ROUTES.USER_PROFILE_PAGE}`} element={<UserProfilePage />} />
                      <Route path={ROUTES.BRAND_ONBOARDING} element={<BrandOnBoardingPage />} />
                      <Route path={ROUTES.NOTIFICATION} element={<NotificationPage />} />
                      <Route element={<BrandRoute />}>
                        {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
                        <Route path="*" element={<BrandWithContext />} />
                      </Route>
                    </Route>
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </SentryRoutes>
              </SidebarLayout>
            </GlobalMementoModalProvider>
          </WalkthroughProvider>
        </main>
      </BrandProvider>
    </UserProvider>
  );
}

function BrandWithContext() {
  return (
    <DataProvider>
      <SentryRoutes>
        <Route path={`${ROUTES.BRAND_ANALYTICS}`} element={<Analytics />} />
        <Route path={ROUTES.BRAND_DASHBOARD} element={<BrandDashboard />} />
        <Route path={ROUTES.BRAND_SETTINGS_ROUTE} element={<BrandSettings />} />
        <Route path={ROUTES.CREATE_CONTROLLED_MAGNET_ROUTE} element={<CreateMagnetPage />} />
        <Route path={ROUTES.CREATE_PUBLIC_MAGNET_ROUTE} element={<CreateMagnetPage />} />
        <Route path={`${ROUTES.EDIT_MAGNET}/:magnetId`} element={<EditMagnetPage />} />
        <Route path={ROUTES.AIRDROP_MEMENTO} element={<AirdropMemento />} />
        <Route path={ROUTES.EDIT_AIRDROP_MEMENTO} element={<AirdropMemento isEditForm />} />
        <Route path={ROUTES.CREATE_POLL} element={<PollPage />} />
        <Route path={ROUTES.EDIT_POLL} element={<PollPage isEditForm />} />
        {/* <Route path={ROUTES.MERCH_DROP} element={<MerchDropPage />} />
  <Route path={ROUTES.EDIT_MERCH_DROP} element={<MerchDropPage isEditForm />} /> */}
        {/* <Route path={ROUTES.CREATE_CLAIM_CODE} element={<ClaimCode />} />
  <Route path={ROUTES.EDIT_CLAIM_CODE} element={<ClaimCode isEditForm />} /> */}
        <Route path={ROUTES.CREATE_SHARE_REQUEST} element={<ShareRequest />} />
        <Route path={ROUTES.EDIT_SHARE_REQUEST} element={<ShareRequest isEditForm />} />
      </SentryRoutes>
    </DataProvider>
  );
}

export default App;

//

// const majorityElement = function(nums:nums[]) {

// };
